import styled from 'styled-components';
import BrandDefaultImage from '../../../../assets/svg/brand_default.svg';
import { Icon } from '../../../../components/common/icon/Icon';
import { MachineApprovalStatus, MachineApprovalStatusType } from '../../../../constants/machine/machine.constant';
import { IMachine } from '../../../../interfaces/Machine.interfaces';
import { IUserOwnTractorCommon } from '../../../../interfaces/OwnTractor.interfaces';
import { isNewProduct } from '../../../../utils/calculator/calculateDate.util';
import { formatKoreanNumber } from '../../../../utils/formatter/formatKoreanNumber.util';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { getRegionByDepth, RegionDepth } from '../../../../utils/getter/getShortAddress.util';
import { ProductChip } from '../components/ProductChip';
import { Button } from '../../../../components/common/buttons/Button';
import { useNavigate } from 'react-router-dom';
import { ownTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useAppDispatch } from '../../../../hooks/store/useStore';
import { userOwnTractorSurveySlice } from '../../../../store/userOwnTractorSurveySlice';
import { ampli } from '../../../../ampli';

type StatusIconType = 'pokable' | 'poked' | 'myTractor';
type StatusIconConfig = {
  icon: { name: 'CircleFork' | 'CircleUser'; fill: string };
  text: string;
  showArrow: boolean;
};
interface UserOwnProductDetailCardProps {
  userOwnProduct: IUserOwnTractorCommon;
  isSalesPending?: boolean;
  isNewBadgeVisible?: boolean;
  showPsOnPendingApproval?: boolean;
  statusIconName?: StatusIconType;
  handleClick?: () => void;
  statusIconClickHandler?: () => void;
  hasEditImageButton?: boolean;
  showBottomSection?: boolean;
}

export const UserOwnProductDetailCard = ({
  userOwnProduct,
  handleClick,
  isSalesPending = false,
  isNewBadgeVisible = false,
  showPsOnPendingApproval = false,
  statusIconName,
  statusIconClickHandler,
  hasEditImageButton = false,
  showBottomSection = true,
}: UserOwnProductDetailCardProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userOwnTractorActions = userOwnTractorSurveySlice.actions;

  const { WAITING, REJECT } = MachineApprovalStatus;

  const getStatusIcon = (status: StatusIconType): StatusIconConfig | null => {
    switch (status) {
      case 'pokable':
        return {
          icon: { name: 'CircleFork', fill: '#34C77E' },
          text: '빠른 찔러보기',
          showArrow: true,
        };
      case 'poked':
        return {
          icon: { name: 'CircleFork', fill: '#858894' },
          text: '신청완료',
          showArrow: false,
        };
      case 'myTractor':
        return {
          icon: { name: 'CircleUser', fill: '#505363' },
          text: '내 트랙터',
          showArrow: false,
        };
      default:
        return null;
    }
  };

  const statusIcon = statusIconName ? getStatusIcon(statusIconName) : null;

  const getBlurEffect = (isSalesPending: boolean, approvalStatus: MachineApprovalStatusType) => {
    const BLURRED = true;
    const NOT_BLURRED = false;
    const DIMMED_TEXT = {
      REJECT: '등록 불가',
      WAITING: '심사중',
      SALE_PENDING: '판매 심사중',
      DEFAULT: '',
    };

    switch (approvalStatus) {
      case WAITING:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.WAITING,
        };

      case REJECT:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.REJECT,
        };

      default:
        if (isSalesPending) {
          return {
            isBlurred: BLURRED,
            dimmedText: DIMMED_TEXT.SALE_PENDING,
          };
        }
        return {
          isBlurred: NOT_BLURRED,
          dimmedText: DIMMED_TEXT.DEFAULT,
        };
    }
  };
  const blurEffect = getBlurEffect(isSalesPending, userOwnProduct.machine?.approvalStatus);
  const { isBlurred, dimmedText } = blurEffect;

  const isNewOwnProduct = isNewProduct(new Date(userOwnProduct.registeredAt), true);

  const handleEditImageButtonClick = () => {
    ampli.pvmEnterOwnTractorImageUploadPage({ from: 'my_own_tractor_page_card_image' });

    dispatch(userOwnTractorActions.changeIsOwnTractorSurveyStartedProperly(true));
    dispatch(userOwnTractorActions.changeOwnTractorIdForEdit({ ownTractorIdForEdit: userOwnProduct.id }));
    navigate(ownTractorPageInfo.imageUploadPage.path);
  };

  return (
    <Container
      onClick={() => {
        if (handleClick) {
          handleClick();
        }
      }}
    >
      <ImageWrap
        onClick={() => {
          if (hasEditImageButton) {
            handleEditImageButtonClick();
          }
        }}
      >
        <Image
          src={userOwnProduct.image || userOwnProduct.brand?.image || BrandDefaultImage}
          isBlurred={isBlurred}
          loading={'lazy'}
        />
        {isBlurred && <DimmedText>{dimmedText}</DimmedText>}
        {isNewBadgeVisible && isNewOwnProduct && (
          <BadgeContainer>
            <ProductChip type={'NEW'} />
          </BadgeContainer>
        )}
        {hasEditImageButton && (
          <EditImageButtonWrapper>
            <Button
              color={'filledWhiteStrong'}
              icon={{
                position: 'left',
                size: 'Large',
                fileName: 'CirclePlus',
                fill: '#34C77E',
              }}
              size={'sm'}
              text={{ content: userOwnProduct.image ? '사진관리' : '사진등록', size: 'lg' }}
              borderType={'round'}
              onClick={handleEditImageButtonClick}
            />
          </EditImageButtonWrapper>
        )}
      </ImageWrap>

      <ProductInfoSection isBlurred={false}>
        <TopSection>
          <Title>
            {getOpenMarketProductTitle({
              brandName: userOwnProduct.brand?.name,
              machine: userOwnProduct.machine as IMachine,
              showPsOnPendingApproval,
            })}
          </Title>
          <LocationAndTimeContainer>
            <Location>
              <Icon name={'Marker'} width={12} height={12} />
              <LocationText>{getRegionByDepth(userOwnProduct.region, RegionDepth.DISTRICT)}</LocationText>
            </Location>
            <YearAndOprationTime>{`${String(userOwnProduct?.manufacturedYear).slice(2, 4)}년식 · ${Number(
              userOwnProduct?.operationTime,
            ).toLocaleString()}시간`}</YearAndOprationTime>
          </LocationAndTimeContainer>
        </TopSection>
        {showBottomSection && (
          <>
            <HorizontalLine />
            <BottomSection>
              <ActionCountBox>
                <CountText>
                  <CountHighlight>{`${formatKoreanNumber(userOwnProduct.pokeCount)}명 `}</CountHighlight>
                  {'신청중'}
                </CountText>
                <CountText>
                  <CountHighlight>{`${formatKoreanNumber(userOwnProduct.views)}명 `}</CountHighlight>
                  {'구경중'}
                </CountText>
              </ActionCountBox>
              {statusIconName && statusIcon ? (
                <StatusIconArea
                  onClick={(e) => {
                    e.stopPropagation();
                    statusIconClickHandler?.();
                  }}
                >
                  <ActionChip>
                    <Icon name={statusIcon.icon.name} width={20} height={20} fill={statusIcon.icon.fill} />
                    <ActionChipText>{statusIcon.text}</ActionChipText>
                    {statusIcon.showArrow && <Icon name={'AngleRight'} width={14} height={14} fill={'#505363'} />}
                  </ActionChip>
                </StatusIconArea>
              ) : null}
            </BottomSection>
          </>
        )}
      </ProductInfoSection>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 140px;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  align-self: stretch;

  gap: 16px;

  cursor: pointer;
`;

const ImageWrap = styled.div`
  min-width: 150px;
  width: 150px;

  position: relative;
  overflow: hidden;
  display: flex;

  align-self: stretch;
  border-radius: 8px;

  background-color: #f2f3f5;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;

  opacity: ${(props) => (props.isBlurred ? 0.4 : 1)};
`;

const BadgeContainer = styled.div`
  position: absolute;

  top: 8px;
  left: 8px;

  z-index: 50;

  display: flex;
  gap: 4px;
`;

const DimmedText = styled.span`
  position: absolute;

  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 20.4px */

  mix-blend-mode: darken;

  opacity: 0.4;

  white-space: nowrap;
`;

const ProductInfoSection = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;
  padding: 8px 0px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  gap: 16px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const LocationAndTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const HorizontalLine = styled.hr`
  width: 100%;

  height: 0px;
  align-self: stretch;
  stroke-width: 1px;
  stroke: #ececf3;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  overflow: hidden;
`;

const Location = styled.div`
  display: flex;
  padding: var(--radius-small, 4px) 6px;
  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  align-items: center;
  flex-shrink: 0;
  gap: 2px;
`;

const LocationText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const YearAndOprationTime = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;

  text-overflow: ellipsis;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ActionCountBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const CountText = styled.div`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
`;

const CountHighlight = styled.span`
  font-weight: 700;
`;

const StatusIconArea = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  flex: 1 0 0;
  align-self: stretch;
`;

const ActionChip = styled.div`
  display: flex;
  height: 32px;
  max-height: 32px;
  padding: 6px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: var(--radius-round, 80px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const ActionChipText = styled.span<{ color?: string }>`
  color: ${(props) => (props.color ? props.color : 'var(--Light-Scheme-Monochrome-offblack, #505363)')};

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const EditImageButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;

  transform: translate(-50%, -50%);
`;
